import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canAccess, canAccessAny } from '../lib/roles';
import { IconBriefcase, IconCart, IconUsers, IconMenu, IconClose } from './Icons';

const logoUrl = process.env.PUBLIC_URL + '/logo.png';

const SIDEBAR_ITEMS = [
  { type: 'link', path: '/', label: 'Dashboard' },
  {
    type: 'group',
    id: 'departments',
    label: 'Departments',
    icon: IconBriefcase,
    children: [
      { path: '/departments', label: 'Departments' },
      { path: '/departments/contacts', label: 'Contacts' },
      { path: '/projects', label: 'Projects' },
      { path: '/taskboard', label: 'Taskboard' },
      { path: '/clients', label: 'Clients' },
      { path: '/notes', label: 'Notes' },
      { path: '/files', label: 'Files' },
    ],
  },
  {
    type: 'group',
    id: 'hr',
    label: 'HR',
    icon: IconUsers,
    children: [
      { path: '/team/members', label: 'Team members' },
      { path: '/hr/salary-chart', label: 'Salary Chart' },
      { path: '/sales/payments', label: 'Payments' },
      { path: '/hr/leave-request', label: 'Leave request' },
      { path: '/team/announcements', label: 'Announcements' },
    ],
  },
  {
    type: 'group',
    id: 'finance',
    label: 'Finance',
    icon: IconCart,
    children: [
      { path: '/expenses', label: 'Expenses' },
      { path: '/sales/invoices', label: 'Invoices' },
    ],
  },
  {
    type: 'link',
    path: '/procurement',
    label: 'Procurement',
  },
  { type: 'link', path: '/tenders', label: 'Tenders and proposals' },
  { type: 'link', path: '/settings', label: 'Settings' },
];

const PAGE_TITLES = {
  '/': 'Dashboard',
  '/clients': 'Clients',
  '/projects': 'Projects',
  '/departments': 'Departments',
  '/departments/contacts': 'Contacts',
  '/taskboard': 'Taskboard',
  '/sales/invoices': 'Invoices',
  '/sales/payments': 'Payments',
  '/team/members': 'Team',
  '/expenses': 'Expenses',
  '/notes': 'Notes',
  '/files': 'Files',
  '/procurement': 'Procurement',
  '/tenders': 'Tenders',
  '/settings': 'Settings',
  '/hr/salary-chart': 'Salary Chart',
  '/hr/leave-request': 'Leave request',
  '/team/announcements': 'Announcements',
};

function getPageTitle(pathname) {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  if (pathname.startsWith('/taskboard/')) return 'Project';
  if (pathname.startsWith('/team/members/')) return 'Member';
  return 'Dashboard';
}

export default function Layout({ children }) {
  const { user, profile, role, signOut } = useAuth();
  const tabPaths = profile?.allowed_tab_paths;
  const navigate = useNavigate();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [openGroupId, setOpenGroupId] = useState('departments');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const displayName = profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';

  useEffect(() => {
    if (!userMenuOpen) return;
    function handleClickOutside(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [userMenuOpen]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileNavOpen) {
      document.body.classList.add('mobile-nav-open');
    } else {
      document.body.classList.remove('mobile-nav-open');
    }
    return () => document.body.classList.remove('mobile-nav-open');
  }, [mobileNavOpen]);

  const handleLogout = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setUserMenuOpen(false);
    console.log('[Layout] Log out clicked', {
      visibilityState: document.visibilityState,
    });
    signOut();
  };

  return (
    <div className={`app${mobileNavOpen ? ' mobile-nav-open' : ''}`}>
      <div className="sidebar-overlay" aria-hidden="true" onClick={() => setMobileNavOpen(false)} />
      <aside className="sidebar sidebar-drawer">
        {SIDEBAR_ITEMS.map((item) => {
          if (item.type === 'link') {
            if (!canAccess(role, item.path, tabPaths)) return null;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}
                end={item.path === '/'}
                onClick={() => setMobileNavOpen(false)}
              >
                {item.label}
              </NavLink>
            );
          }

          if (item.type === 'group') {
            const childPaths = item.children.map((c) => c.path);
            if (!canAccessAny(role, childPaths, tabPaths)) return null;
            const GroupIcon = item.icon || React.Fragment;
            const isOpen = openGroupId === item.id;

            return (
              <div key={item.id}>
                <button
                  type="button"
                  className="sidebar-item sidebar-group-header"
                  onClick={() => setOpenGroupId(isOpen ? null : item.id)}
                >
                  <span className="sidebar-group-icon">
                    <GroupIcon />
                  </span>
                  <span className="sidebar-group-label">{item.label}</span>
                  <span className={`sidebar-group-caret${isOpen ? ' open' : ''}`}>
                    ˅
                  </span>
                </button>
                <div
                  className={`sidebar-group-children${
                    isOpen ? '' : ' collapsed'
                  }`}
                >
                  {item.children
                    .filter((child) => canAccess(role, child.path, tabPaths))
                    .map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        end={child.path === '/departments'}
                        className={({ isActive }) =>
                          `sidebar-item sidebar-child${isActive ? ' active' : ''}`
                        }
                        onClick={() => setMobileNavOpen(false)}
                      >
                        {child.label}
                      </NavLink>
                    ))}
                </div>
              </div>
            );
          }

          return null;
        })}
      </aside>

      <div className="main">
        <header className="header">
          <div className="header-left">
            <button
              type="button"
              className="header-menu-btn"
              onClick={() => setMobileNavOpen((o) => !o)}
              aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileNavOpen}
            >
              {mobileNavOpen ? <IconClose /> : <IconMenu />}
            </button>
            <img src={logoUrl} alt="CDT" className="header-logo" />
            <span className="header-title">{getPageTitle(location.pathname)}</span>
          </div>
          <div className="header-right">
            <div className="user-info-wrap" ref={userMenuRef}>
              <button
                type="button"
                className="user-info"
                onClick={() => setUserMenuOpen((o) => !o)}
                aria-expanded={userMenuOpen}
                aria-haspopup="menu"
              >
                <div className="avatar">{(displayName || 'U')[0].toUpperCase()}</div>
                <span className="user-info-name">{displayName}</span>
              </button>
              {userMenuOpen && (
                <div className="user-menu" role="menu">
                  <div className="user-menu-role" title="Current role from profile">Role: {role}</div>
                  {user?.id && (
                    <button type="button" role="menuitem" onClick={() => { navigate(`/team/members/${user.id}`); setUserMenuOpen(false); }}>
                      Profile
                    </button>
                  )}
                  <button type="button" role="menuitem" onClick={handleLogout}>Log out</button>
                </div>
              )}
            </div>
          </div>
        </header>
        {children}
      </div>
    </div>
  );
}
